/**
 * One investment pot on the timeline's ledger (TIMELINE "How the ledger moves", steps 3, 4 and 6):
 * it opens on its start date holding its initial balance; each payment lands on the day it is made,
 * at any frequency (the saver's own amount grossed up by relief, plus the employer's match); a
 * withdrawal takes what is there and never more, so the pot stops at 0; on the last day of each
 * month it grows by the item's rate (else the plan's) on what it held at the end of the month
 * before, so a payment earns from the month after it is made.
 */
import { addDays, addMonths, startOfMonth, type Day } from '../core/calendar.ts';
import { abs, add, dec, lt, max, min, mul, negate, sub, ZERO, type Decimal } from '../core/decimal.ts';
import { fieldsOn, paidOn } from '../models/transaction.ts';
import type { Transaction } from '../types/domain.ts';
import { calculateTotalInvestment } from './investment-projector.ts';
import { monthlyRate } from './loan-math.ts';
import { scheduledDates } from './payment-calculator.ts';

/**
 * A drawn-down pot's growth on its last few pennies shrinks geometrically; below half a penny it is
 * empty, so the pot stays at 0 instead of carrying a vanishing remainder.
 */
const HALF_PENNY = dec('0.005');

export interface PotPayment {
  readonly date: Day;
  /** What left cash: the saver's own amount, or minus what a withdrawal paid into cash. */
  readonly cash: Decimal;
}

export interface PotRun {
  /** The pot at the end of each of the sampled days, in the order given. */
  readonly values: readonly Decimal[];
  readonly payments: readonly PotPayment[];
}

/** Runs `investment`'s pot through the last of `samples` (ascending days), or `through` when later. */
export function runPot(
  investment: Transaction,
  incomes: readonly Transaction[],
  fallbackReturnPercent: Decimal,
  samples: readonly Day[],
  through: Day = samples[samples.length - 1] ?? investment.startDate,
): PotRun {
  const last = samples.reduce((a, b) => (b > a ? b : a), through);
  const rateIn = (monthEnd: Day) => monthlyRate(fieldsOn(investment, monthEnd).interestRate ?? fallbackReturnPercent);
  const dates = scheduledDates(investment, addDays(last, 1));
  const values: Decimal[] = [];
  const payments: PotPayment[] = [];
  let balance: Decimal = ZERO;
  let opening: Decimal = ZERO;
  let exists = false;
  let next = 0;
  let sampled = 0;
  const sampleThrough = (day: Day) => {
    while (sampled < samples.length && samples[sampled]! <= day) {
      values.push(exists ? balance : ZERO);
      sampled += 1;
    }
  };
  const openBy = (monthEnd: Day) => {
    if (exists || monthEnd < investment.startDate) return;
    sampleThrough(addDays(investment.startDate, -1));
    exists = true;
    balance = investment.initialBalance ?? ZERO;
  };

  const first = startOfMonth(samples[0] !== undefined && samples[0] < investment.startDate ? samples[0] : investment.startDate);
  for (let month = first; month <= last; month = addMonths(month, 1)) {
    const monthEnd = addDays(addMonths(month, 1), -1);
    openBy(monthEnd);
    while (next < dates.length && dates[next]! <= monthEnd) {
      const date = dates[next++]!;
      sampleThrough(addDays(date, -1));
      const own = paidOn(investment, date);
      if (!lt(own, ZERO)) {
        balance = add(balance, calculateTotalInvestment(investment, date, incomes));
        payments.push({ date, cash: own });
      } else {
        const withdrawn = min(negate(own), max(balance, ZERO));
        balance = sub(balance, withdrawn);
        payments.push({ date, cash: negate(withdrawn) });
      }
    }
    sampleThrough(addDays(monthEnd, -1));
    if (exists) balance = add(balance, mul(opening, rateIn(monthEnd)));
    if (lt(abs(balance), HALF_PENNY)) balance = ZERO;
    opening = exists ? balance : ZERO;
    sampleThrough(monthEnd);
  }
  sampleThrough(last);
  return { values, payments };
}
